<template>
  <div class="w-full">
    <h1 class="mb-8 text-3xl font-bold">
      Języki
    </h1>
    <div class="mb-6 flex items-center justify-between">
      <search-filter
        v-model="form.search"
        class="mr-4 w-full max-w-md"
        @reset="reset"
      />

      <button class="btn-indigo" @click="modalNew = true">
        Dodaj język
      </button>
      <modal
        :open="modalNew"
        title="Dodaj język"
        @close="modalNew = false"
      >
        <new-language @success="modalNew = false" />
      </modal>
    </div>
    <div class="overflow-x-auto rounded-sm bg-white shadow-sm">
      <table
        class="w-full whitespace-nowrap"
        :class="{ 'is-dnd-dragging': dragging }"
      >
        <thead>
          <tr class="text-left font-bold">
            <th class="w-px px-4 pb-4 pt-6" />
            <th class="px-6 pb-4 pt-6">
              Nazwa
            </th>
            <th class="px-6 pb-4 pt-6">
              Obrazek
            </th>
            <th class="px-6 pb-4 pt-6">
              Specjaliści
            </th>
            <th class="px-6 pb-4 pt-6">
              Aktywny
            </th>
            <th class="px-6 pb-4 pt-6" />
          </tr>
        </thead>
        <draggable
          v-if="localLanguages.length"
          v-model="localLanguages"
          tag="tbody"
          item-key="id"
          handle=".drag-handle"
          ghost-class="dnd-drop-ghost"
          chosen-class="dnd-chosen"
          @start="onDragStart"
          @end="onDragEnd"
        >
          <template #item="{ element: language }">
            <tr
              :key="language.id"
              class="focus-within:bg-gray-100 hover:bg-gray-100"
            >
              <td class="w-px border-t px-4">
                <div class="drag-handle cursor-grab active:cursor-grabbing" title="Przeciągnij, aby zmienić kolejność">
                  <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                  </svg>
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  {{ language.name }}
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  <img
                    v-if="language.image_url"
                    :src="language.image_url"
                    :alt="language.name"
                    class="h-6 w-8 rounded object-cover"
                  >
                  <span v-else class="text-gray-400">Brak obrazka</span>
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  {{ language.specialists_count ?? 0 }}
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  <toggle-switch
                    :model-value="language.active"
                    :disabled="togglingId === language.id"
                    @update:model-value="toggleActive(language)"
                  />
                </div>
              </td>
              <td class="w-px border-t">
                <div class="ml-4 flex shrink-0 items-center gap-2 px-4">
                  <Link
                    v-if="language.id"
                    class="btn-action-edit"
                    :href="editRoute(language.id)"
                    title="Edytuj"
                  >
                    <icon name="edit" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                    Edytuj
                  </Link>
                  <button
                    v-if="language.id"
                    type="button"
                    class="btn-action-delete"
                    title="Usuń"
                    @click.stop="destroy(language)"
                  >
                    <icon name="trash" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                    Usuń
                  </button>
                </div>
              </td>
            </tr>
          </template>
        </draggable>
        <tbody v-else>
          <tr>
            <td class="border-t px-6 py-4" colspan="6">
              Nie znaleziono języków.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination :meta="languages.meta" />
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import mapValues from 'lodash/mapValues';
import Pagination from '@/Shared/Pagination.vue';
import pickBy from 'lodash/pickBy';
import SearchFilter from '@/Shared/SearchFilter.vue';
import Modal from '@/Shared/Modal.vue';
import NewLanguage from '@/Pages/Admin/Languages/_New.vue';
import throttle from 'lodash/throttle';
import { createLanguageRouteHelper } from '@/utils/createLanguageRouteHelper';
import { createLayoutHelper } from '@/utils/layoutHelper';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import draggable from 'vuedraggable';
import axios from 'axios';

export default {
  components: {
    Link,
    Icon,
    Pagination,
    SearchFilter,
    Modal,
    NewLanguage,
    ToggleSwitch,
    draggable,
  },
  layout: createLayoutHelper,
  props: {
    languages: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {
        search: this.filters.search,
      },
      modalNew: false,
      localLanguages: [...this.languages.data],
      dragging: false,
      togglingId: null,
    };
  },
  computed: {
    routes() {
      return createLanguageRouteHelper(null, this.$routes);
    },
    languagesRoute() {
      return this.routes.index();
    },
  },
  watch: {
    form: {
      handler: throttle(function () {
        const query = pickBy(this.form);
        this.$inertia.get(
          this.languagesRoute,
          Object.keys(query).length ? query : {},
          {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            only: ['languages'],
          },
        );
      }, 150),
      deep: true,
    },
    '$page.url'() {
      if (this.modalNew) this.modalNew = false;
    },
    'languages.data': {
      handler(newData) {
        this.localLanguages = [...newData];
      },
      deep: true,
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null);
    },
    editRoute(languageId) {
      return languageId ? this.routes.edit(languageId) : '#';
    },
    toggleActive(language) {
      if (!language.id || this.togglingId) return;

      this.togglingId = language.id;
      const target = this.localLanguages.find((item) => item.id === language.id);
      if (target) target.active = !target.active;

      this.$inertia.put(
        this.routes.update(language.id),
        { language: { active: target ? target.active : !language.active } },
        {
          preserveScroll: true,
          preserveState: true,
          only: ['languages'],
          onError: () => {
            if (target) target.active = !target.active;
          },
          onFinish: () => {
            this.togglingId = null;
          },
        },
      );
    },
    destroy(language) {
      if (!language.id) return;
      if (confirm('Czy na pewno chcesz usunąć ten język?')) {
        this.$inertia.delete(this.routes.destroy(language.id), {
          preserveScroll: true,
        });
      }
    },
    onDragStart() {
      this.dragging = true;
    },
    async onDragEnd() {
      this.dragging = false;

      const ids = this.localLanguages.map((language) => language.id);

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        await axios.post(this.routes.reorder(), { ids }, {
          headers: { 'X-CSRF-Token': csrfToken },
        });

        this.$inertia.reload({ only: ['languages'] });
      } catch (error) {
        console.error('Failed to reorder languages:', error);
        this.localLanguages = [...this.languages.data];
        alert('Nie udało się zmienić kolejności języków. Spróbuj ponownie.');
      }
    },
  },
};
</script>
